import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { AlertCircle, Clock, Send, Save, FileText, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import VCRStage1 from '../components/VCR/Stage1';
import VCRStage2 from '../components/VCR/Stage2';
import VCRStage3 from '../components/VCR/Stage3';
import VCRStage4 from '../components/VCR/Stage4';
import RTACLetters from '../components/VCR/RTACLetters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

export default function VCR() {
  const [vcrReports, setVcrReports] = useState([]);
  const [activeReport, setActiveReport] = useState(null);
  const [currentStage, setCurrentStage] = useState(1);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchVCRReports();
  }, []);

  const fetchVCRReports = async () => {
    try {
      const response = await axios.get(`${API}/vcr`, getAuthHeaders());
      setVcrReports(response.data);
    } catch (error) {
      toast.error('Failed to load VCR reports');
    }
  };

  const startNewReport = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/vcr`,
        { started_at: new Date().toISOString() },
        getAuthHeaders()
      );
      setActiveReport(response.data);
      setCurrentStage(1);
      toast.success('New VCR started');
    } catch (error) {
      console.error('VCR Creation Error:', error);
      toast.error('Failed to start VCR');
    } finally {
      setLoading(false);
    }
  };

  const deleteReport = async (reportId, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this VCR report? This cannot be undone.')) return;
    
    try {
      await axios.delete(`${API}/vcr/${reportId}`, getAuthHeaders());
      toast.success('VCR deleted');
      fetchVCRReports();
    } catch (error) {
      toast.error('Failed to delete VCR');
    }
  };

  const openExistingReport = (report) => {
    setActiveReport(report);
    setCurrentStage(report.current_stage || 1);
  };

  const closeReport = () => {
    setActiveReport(null);
    fetchVCRReports();
  };

  if (activeReport) {
    return (
      <div className="space-y-6" data-testid="vcr-form">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-slate-800">Vehicle Collision Report</h1>
            <p className="text-slate-600">VCR ID: {activeReport.id}</p>
          </div>
          <Button variant="outline" onClick={closeReport}>
            Back to List
          </Button>
        </div>

        {/* Stage Progress */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              {[1, 2, 3, 4].map((stage) => (
                <div key={stage} className="flex flex-col items-center">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    currentStage === stage ? 'bg-blue-600 text-white' :
                    currentStage > stage ? 'bg-green-600 text-white' :
                    'bg-slate-200 text-slate-600'
                  }`}>
                    {stage}
                  </div>
                  <p className="text-xs mt-2 text-slate-600">
                    {stage === 1 && '1st Hour'}
                    {stage === 2 && '24 Hours'}
                    {stage === 3 && 'Pictures'}
                    {stage === 4 && 'Investigation'}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Stage Forms */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            {currentStage === 1 && (
              <VCRStage1 
                report={activeReport}
                onNext={() => setCurrentStage(2)}
                onSave={fetchVCRReports}
                onClose={closeReport}
              />
            )}
            {currentStage === 2 && (
              <VCRStage2 
                report={activeReport}
                onNext={() => setCurrentStage(3)}
                onPrevious={() => setCurrentStage(1)}
                onSave={fetchVCRReports}
                onClose={closeReport}
              />
            )}
            {currentStage === 3 && (
              <VCRStage3 
                report={activeReport}
                onNext={() => setCurrentStage(4)}
                onPrevious={() => setCurrentStage(2)}
                onSave={fetchVCRReports}
                onClose={closeReport}
              />
            )}
            {currentStage === 4 && (
              <VCRStage4 
                report={activeReport}
                onPrevious={() => setCurrentStage(3)}
                onSave={fetchVCRReports}
                onClose={closeReport}
              />
            )}
          </div>

          {/* RTAC Letters Panel */}
          <div className="lg:col-span-1">
            <RTACLetters 
              vcrId={activeReport.id}
              completedLetters={activeReport.completed_letters || []}
              onLetterComplete={fetchVCRReports}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="vcr-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-slate-800 dark:text-slate-100 mb-2">Vehicle Collision Reports</h1>
          <p className="text-slate-600 dark:text-slate-400">Manage multi-stage collision incident reports</p>
        </div>
        <Button onClick={startNewReport} disabled={loading} data-testid="start-vcr-button">
          <FileText className="h-4 w-4 mr-2" />
          Start New VCR
        </Button>
      </div>

      {/* Active/Pending VCRs */}
      <div className="grid grid-cols-1 gap-4">
        {vcrReports.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-slate-500">No VCR reports yet. Click "Start New VCR" to begin.</p>
            </CardContent>
          </Card>
        ) : (
          vcrReports.map((report) => (
            <Card 
              key={report.id} 
              className="cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => openExistingReport(report)}
              data-testid={`vcr-report-${report.id}`}
            >
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <div>
                    <span>{report.employee_name || 'Unnamed Report'}</span>
                    <p className="text-sm font-normal text-slate-600">Started: {format(new Date(report.started_at), 'PPpp')}</p>
                  </div>
                  <Badge variant={report.status === 'completed' ? 'default' : 'secondary'}>
                    Stage {report.current_stage || 1} / 4
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <p className="text-sm text-slate-600">
                    {report.incident_date && `Incident: ${format(new Date(report.incident_date), 'MMM d, yyyy')}`}
                  </p>
                  <div className="flex items-center space-x-2">
                    {report.status !== 'completed' ? (
                      <Badge variant="outline" className="bg-amber-50">
                        In Progress
                      </Badge>
                    ) : (
                      <Badge variant="default" className="bg-green-600">
                        Completed
                      </Badge>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => deleteReport(report.id, e)}
                      data-testid={`delete-vcr-${report.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
